import { HEADER_OFFSET_CONFIG } from '~/composables/useDragToBottom/constants';
import { usePropertiesListingHeader } from '~/composables/usePropertiesListingHeader';

import { HEADER_REVEAL_THRESHOLD_PX, SNAP_RESTING_PX } from './constants';
import { SheetLevel } from './types';

// Controlador da navbar no ciclo do bottom sheet. TODAS as regras de
// visibilidade da navbar vivem aqui; o orquestrador apenas reporta eventos.
//
// Regras de negocio:
// - No peek e no half-open a navbar fica oculta (segue o nivel).
// - Ao abrir a lista (CLOSED) a navbar fica CONGELADA: nunca aparece sozinha
//   (nem durante o drag/snap de abertura). A partir dai so o usuario a controla:
//   - scroll de leitura: descer esconde, subir mostra;
//   - puxao curto pra baixo que NAO comete a descida (snap volta ao CLOSED)
//     mostra a barra — e o gesto de "chamar" a navbar no fullscreen.

export interface SheetHeaderController {
	syncToLevel(level: SheetLevel): void;
	trackReadingScroll(delta: number, isReading: boolean): void;
	revealOnPullBackToClosed(input: {
		startY: number;
		y: number;
		target: number;
		closedY: number;
	}): void;
	isVisible(): boolean;
	resetToVisible(): void;
}

export function useSheetHeader(
	isEnabled: () => boolean
): SheetHeaderController {
	const { headerDragOffsetY } = usePropertiesListingHeader();

	// Movimento liquido do scroll de leitura no sentido atual. A navbar so vira
	// quando o acumulado passa de HEADER_REVEAL_THRESHOLD_PX; reversao de
	// sentido zera (jitter nao vira toggle, sem debounce/delay).
	let readingScrollDelta = 0;

	function show(): void {
		headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MIN_OFFSET;
	}

	function hide(): void {
		headerDragOffsetY.value = HEADER_OFFSET_CONFIG.MAX_OFFSET;
	}

	// Peek/half-open: oculta. CLOSED: CONGELADA (nao mexe) — nunca aparece
	// sozinha ao chegar no fullscreen (ex.: half->closed). A partir do CLOSED so
	// o usuario a controla (scroll de leitura / puxao pra baixo).
	function syncToLevel(level: SheetLevel): void {
		if (!isEnabled()) return;
		if (level === SheetLevel.CLOSED) return;
		hide();
	}

	// Hide-on-scroll da leitura. Fora da leitura a navbar nao reage ao scroll
	// (faixa do drawer), e o acumulador recomeca do zero na proxima leitura.
	function trackReadingScroll(delta: number, isReading: boolean): void {
		if (!isEnabled()) return;
		if (!isReading) {
			readingScrollDelta = 0;
			return;
		}

		if (delta !== 0 && Math.sign(delta) !== Math.sign(readingScrollDelta)) {
			readingScrollDelta = 0;
		}
		readingScrollDelta += delta;

		if (readingScrollDelta >= HEADER_REVEAL_THRESHOLD_PX) {
			hide(); // scroll p/ baixo
			readingScrollDelta = 0;
		} else if (readingScrollDelta <= -HEADER_REVEAL_THRESHOLD_PX) {
			show(); // scroll p/ cima
			readingScrollDelta = 0;
		}
	}

	// A abertura (gesto pra cima rumo ao CLOSED) nao passa em `movedDown`,
	// entao a navbar continua oculta ao abrir a lista.
	function revealOnPullBackToClosed(input: {
		startY: number;
		y: number;
		target: number;
		closedY: number;
	}): void {
		if (!isEnabled()) return;
		const { startY, y, target, closedY } = input;
		const hasMovedDown = y < startY - SNAP_RESTING_PX;
		const hasStartedAtClosed = startY >= closedY - SNAP_RESTING_PX;
		const willSnapBackToClosed = target >= closedY - SNAP_RESTING_PX;
		if (hasMovedDown && hasStartedAtClosed && willSnapBackToClosed) show();
	}

	// Navbar visivel = ainda nao escondida por completo (MAX_OFFSET).
	function isVisible(): boolean {
		return headerDragOffsetY.value < HEADER_OFFSET_CONFIG.MAX_OFFSET;
	}

	// Estado de descanso ao entrar/sair do modo sheet (troca de breakpoint).
	// Sem guard de `isEnabled`: ao voltar pro desktop a navbar deve reaparecer.
	function resetToVisible(): void {
		show();
	}

	return {
		syncToLevel,
		trackReadingScroll,
		revealOnPullBackToClosed,
		isVisible,
		resetToVisible
	};
}
